import React, { useEffect, useState } from 'react'
import { IconButton } from '~/components/IconButton'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'

export function UploadProgress({ progressPercent, tight }: { progressPercent: number; tight?: boolean }) {
  const percentage = `${Math.round(progressPercent)}%`
  const text = tight ? percentage : `${percentage} uploaded`

  return (
    <div className={`flex flex-row gap-2 items-center rounded text-sm ${tight ? '' : 'p-2 border border-secondary'}`}>
      <span>{text}</span>
      <div className="w-24 bg-tertiary rounded-full h-1">
        <div className="bg-success-inverse h-1 rounded-full" style={{ width: `${progressPercent}%` }} />
      </div>
    </div>
  )
}

export function StartRecordingIcon({ size }: { size?: 'sm' | 'lg' }) {
  return size === 'sm' ? (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="10" className="fill-danger-icon" />
    </svg>
  ) : (
    <div className="w-10 h-10 relative">
      <div className="w-9 h-9 left-0.5 top-0.5 absolute bg-danger-icon rounded-full" />
    </div>
  )
}

export function PauseButton({ onClick, size }: { onClick: () => void; size: 'sm' | 'lg' }) {
  return size === 'sm' ? (
    <button
      onClick={onClick}
      className={`
        h-9 p-2 rounded-md justify-start items-center gap-4 inline-flex group
        hover:scale-125 transition-transform duration-100
      `}
    >
      <span className="sr-only">Pause capture</span>
      <div className="w-5 h-5 relative">
        <div className="w-[18px] h-[18px] left-px top-px absolute bg-primary rounded-full border border-secondary" />
        <div
          className={`
            w-2.5 h-2.5 left-[5px] top-[5px] absolute bg-danger-icon rounded-sm
            group-active:scale-110 transition-all duration-100
          `}
        />
      </div>
    </button>
  ) : (
    <button onClick={onClick} className="w-10 h-10 flex-col justify-start items-start inline-flex">
      <span className="sr-only">Pause capture</span>
      <div className="w-10 h-10 relative">
        <div className="w-[46px] h-[46px] left-[-3px] top-[-3px] absolute bg-white rounded-full border-2 border-white" />
        <div className="w-10 h-10 left-0 top-0 absolute bg-white rounded-full border-2 border-[#c6cdd2]" />
        <div className="w-5 h-5 left-[10px] top-[10px] absolute bg-[#e22f4f] rounded-[5px]" />
      </div>
    </button>
  )
}

export function DownloadButton({ onClick, variant }: { onClick: () => void; variant?: 'transparent' | 'just-icon' }) {
  return (
    <IconButton
      icon={<ArrowDownTrayIcon />}
      screenReaderText="Download captured meeting"
      tooltip="Download captured meeting"
      onClick={onClick}
      variant={variant}
    />
  )
}

export function TickUpdated({ currentValue }: { currentValue: (now: number) => React.ReactNode }) {
  const [value, setValue] = useState(currentValue(Date.now()))

  // Tyler says: I'm not *thrilled* with the use of hooks here, but
  // because the state is purely derived from the start time, and
  // extremely transient, it seems okay.
  // The truth is we really just need a way to tickle the render
  // once a second...
  useEffect(() => {
    const interval = setInterval(() => {
      setValue(currentValue(Date.now()))
    }, 1000)

    return () => clearInterval(interval)
  }, [currentValue])

  return value
}

export function PlayingDisclosureView() {
  return <div className="flex flex-row gap-2 items-center text-primary text-sm">Playing capture disclosure...</div>
}

export function ContainerOverFrequencyVisualizer({ children }: { children: React.ReactNode }) {
  return <div className="absolute inset-0 flex items-center justify-center text-primary-inverse">{children}</div>
}
